import { useFormik } from 'formik'

import { FC, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import {
	AreaSelect,
	AreaSelectHandle,
	ClientSelect,
	ClientSelectHandle,
	CountrySelect,
	IUfinetSelectOption,
	UfinetActionButton,
	UfinetActionButtonHandle,
	UfinetInput,
	UfinetSelect,
	emptyUfinetSelectOption,
} from 'ufinet-web-components'
import {
	AreaType,
	AuthContext,
	ZoneTypeOptions,
	equipmentCostsService,
	onFormikChanges,
	onFormikNumberChanges,
} from 'ufinet-web-functions'

import * as Yup from 'yup'

type Props = {
	hideModal: Function
	findCosts: Function
}

type EquipmentModalData = {
	countrySelect: IUfinetSelectOption
	areaSelect: IUfinetSelectOption & { color: string }
	clientSelect: IUfinetSelectOption
	zoneSelect: IUfinetSelectOption
	serviceTypeSelect: IUfinetSelectOption
	technicalAttributeSelect: IUfinetSelectOption
	transmissionMediumSelect: IUfinetSelectOption
	minCap: string
	maxCap: string
	cost: string
	costMO: string
}

const NewEquipmentModalBody: FC<Props> = ({ hideModal, findCosts }: Props) => {
	const intl = useIntl()

	const areaRef = useRef<AreaSelectHandle>(null)
	const clientRef = useRef<ClientSelectHandle>(null)
	const actionButtonRef = useRef<UfinetActionButtonHandle>(null)

	const authData = useContext(AuthContext)
	const _equipmentCostsService = useMemo(() => equipmentCostsService(authData, intl), [authData, intl])

	// Estados para manejar las opciones de los selects
	const [technicalAttributeOptions, setTechnicalAttributeOptions] = useState<any[]>([])
	const [transmissionMediumOptions, setTransmissionMediumOptions] = useState<any[]>([])
	const [_loadingOptions, _setLoadingOptions] = useState(false)

	const _onCostsFailedToFetch = (): void => {
		toast.error(intl.formatMessage({ id: 'COSTS.FETCH.ERROR' }))
	}

	const sendData = (value: EquipmentModalData) => {
		actionButtonRef.current?.changeActionStatus(true)
		_equipmentCostsService
			.create({
				countryId: value.countrySelect.value,
				countryName: value.countrySelect.label,
				networkAreaName: value.areaSelect.value,
				zoneType: value.zoneSelect.value,
				serviceTypeId: value.serviceTypeSelect.value,
				technicalAttributeId: value.technicalAttributeSelect.value,
				transmissionMediumId: value.transmissionMediumSelect.value,
				minimumCapacity: value.minCap,
				maximumCapacity: value.maxCap,
				cost: value.cost,
				laborCost: value.costMO,
				clientId: value.clientSelect.value,
				lang: intl.locale,
			})
			.then(() => {
				findCosts()
				hideModal()
			})
			.catch(console.error)
			.finally(() => actionButtonRef.current?.changeActionStatus(false))
	}
	const dataFormSchema = Yup.object().shape({
		countrySelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		zoneSelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		areaSelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		clientSelect: Yup.object().shape({ label: Yup.string().notRequired() }),
		serviceTypeSelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		technicalAttributeSelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		transmissionMediumSelect: Yup.object().shape({
			label: Yup.string().required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		}),
		minCap: Yup.number()
			.test('', intl.formatMessage({ id: 'ERROR.POSITIVE' }), (value: any) => value >= 0)
			.required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		maxCap: Yup.number()
			.test('', intl.formatMessage({ id: 'ERROR.POSITIVE' }), (value: any) => value >= 0)
			.required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		cost: Yup.number()
			.test('', intl.formatMessage({ id: 'ERROR.POSITIVE' }), (value: any) => value >= 0)
			.required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
		costMO: Yup.number()
			.test('', intl.formatMessage({ id: 'ERROR.POSITIVE' }), (value: any) => value >= 0)
			.required(intl.formatMessage({ id: 'ERROR.REQUIRED' })),
	})

	const formInitialValues = useMemo<EquipmentModalData>(
		() => ({
			countrySelect: emptyUfinetSelectOption,
			areaSelect: { label: '', value: '', color: '' },
			clientSelect: emptyUfinetSelectOption,
			zoneSelect: emptyUfinetSelectOption,
			serviceTypeSelect: emptyUfinetSelectOption,
			technicalAttributeSelect: emptyUfinetSelectOption,
			transmissionMediumSelect: emptyUfinetSelectOption,
			minCap: '',
			maxCap: '',
			cost: '',
			costMO: '',
		}),
		[]
	)

	const formik = useFormik<EquipmentModalData>({
		initialValues: formInitialValues,
		validationSchema: dataFormSchema,
		onSubmit: sendData,
		validateOnChange: false,
		validateOnBlur: false,
	})

	const onCountryChange = (country?: IUfinetSelectOption) => {
		onChange(formik, 'countrySelect')(country || emptyUfinetSelectOption)
		onChange(formik, 'areaSelect')(emptyUfinetSelectOption)
		onChange(formik, 'clientSelect')(emptyUfinetSelectOption)
		if (country) {
			areaRef.current?.fillSelect([country.value])
			clientRef.current?.fillSelect([country.value], [])
		}
	}

	const onServiceTypeChange = async (serviceType?: IUfinetSelectOption) => {
		onChange(formik, 'serviceTypeSelect')(serviceType || emptyUfinetSelectOption)
		// Limpiar los selects dependientes cuando cambia el tipo de servicio
		onChange(formik, 'technicalAttributeSelect')(emptyUfinetSelectOption)
		onChange(formik, 'transmissionMediumSelect')(emptyUfinetSelectOption)

		// Cargar las nuevas opciones
		if (serviceType?.value && _equipmentCostsService) {
			_setLoadingOptions(true)
			try {
				const response = await _equipmentCostsService.getCombinationFieldsByServiceType(parseInt(serviceType.value))

				const techOptions =
					response.technicalAttribute?.map((attr: any) => ({
						label: attr.name,
						value: attr.id.toString(),
					})) || []

				const transOptions =
					response.transmissionMedium?.map((medium: any) => ({
						label: medium.name,
						value: medium.id.toString(),
					})) || []

				setTechnicalAttributeOptions(techOptions)
				setTransmissionMediumOptions(transOptions)
			} catch (error) {
				console.error('Error loading options:', error)
				setTechnicalAttributeOptions([])
				setTransmissionMediumOptions([])
			} finally {
				_setLoadingOptions(false)
			}
		} else {
			setTechnicalAttributeOptions([])
			setTransmissionMediumOptions([])
		}
	}

	const onChange = useCallback(onFormikChanges, [])
	const onNumberChange = useCallback(onFormikNumberChanges, [])

	return (
		<form onSubmit={formik.handleSubmit} className="container p-15 h-100 d-flex flex-column justify-content-center">
			<div className="row">
				<CountrySelect
					requiredIcon
					className="col-4"
					onChange={(c) => onCountryChange(c as IUfinetSelectOption)}
					value={formik.values.countrySelect}
					error={formik.errors.countrySelect?.label}
				/>
				<AreaSelect
					ref={areaRef}
					requiredIcon
					className="col-4"
					areaType={AreaType.OPTICAL_FIBER_AREA}
					value={formik.values.areaSelect}
					error={formik.errors.areaSelect?.label}
					isDisabled={!formik.values.countrySelect.value}
					onChange={onChange(formik, 'areaSelect')}
				/>
				<ClientSelect
					ref={clientRef}
					requiredIcon={false}
					className="col-4"
					value={formik.values.clientSelect}
					error={formik.errors.clientSelect}
					isDisabled={!formik.values.countrySelect.value}
					onChange={onChange(formik, 'clientSelect')}
				/>
			</div>
			<div className="row pt-4">
				<UfinetSelect
					requiredIcon
					className="col-6"
					labelTitle={intl.formatMessage({ id: 'COST.SCOPE' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.SCOPE' })}
					error={formik.errors.zoneSelect?.label}
					isClearable
					onChange={onChange(formik, 'zoneSelect')}
					value={formik.values.zoneSelect?.label && formik.values.zoneSelect}
					options={ZoneTypeOptions}
					placeholder={intl.formatMessage({ id: 'COST.SCOPE' })}
				/>
				<UfinetInput
					requiredIcon
					error={formik.errors.minCap}
					type="decimal"
					labelTitle={intl.formatMessage({ id: 'COST.MIN_CAPACITY' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.MIN_CAPACITY' })}
					className="col-6"
					onChange={onNumberChange(formik, 'minCap')}
					value={formik.values.minCap}
					solid={false}
				/>
			</div>
			<div className="row pt-4">
				<UfinetInput
					requiredIcon
					error={formik.errors.maxCap}
					type="decimal"
					labelTitle={intl.formatMessage({ id: 'COST.MAX_CAPACITY' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.MAX_CAPACITY' })}
					className="col-4"
					onChange={onNumberChange(formik, 'maxCap')}
					value={formik.values.maxCap}
					solid={false}
				/>
				<UfinetInput
					requiredIcon
					error={formik.errors.cost}
					type="decimal"
					labelTitle={intl.formatMessage({ id: 'COST.COST' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.COST' })}
					className="col-4"
					onChange={onNumberChange(formik, 'cost')}
					value={formik.values.cost}
					solid={false}
				/>
				<UfinetInput
					requiredIcon
					error={formik.errors.costMO}
					type="decimal"
					labelTitle={intl.formatMessage({ id: 'COST.COSTMO' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.COSTMO' })}
					className="col-4"
					onChange={onNumberChange(formik, 'costMO')}
					value={formik.values.costMO}
					solid={false}
				/>
			</div>
			<div className="row pt-4">
				<UfinetSelect
					requiredIcon
					className="col-4"
					labelTitle={intl.formatMessage({ id: 'COST.SERVICE_TYPE' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.SERVICE_TYPE' })}
					error={formik.errors.serviceTypeSelect?.label}
					isClearable
					onChange={(serviceType) => onServiceTypeChange(serviceType as IUfinetSelectOption)}
					value={formik.values.serviceTypeSelect?.label && formik.values.serviceTypeSelect}
					options={[
						{
							label: intl.formatMessage({ id: 'COST.SERVICE_TYPE_CAPACITY', defaultMessage: 'Capacidad' }),
							value: '100000007',
						},
						{
							label: intl.formatMessage({ id: 'COST.SERVICE_TYPE_INTERNET', defaultMessage: 'Internet' }),
							value: '100000001',
						},
					]}
					placeholder={intl.formatMessage({
						id: 'COST.SELECT_SERVICE_TYPE',
						defaultMessage: 'Seleccione tipo de servicio',
					})}
				/>
				<UfinetSelect
					requiredIcon
					className="col-4"
					labelTitle={intl.formatMessage({ id: 'COST.TECHNICAL_ATTRIBUTE' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.TECHNICAL_ATTRIBUTE' })}
					error={formik.errors.technicalAttributeSelect?.label}
					isClearable
					isDisabled={!formik.values.serviceTypeSelect?.value}
					onChange={onChange(formik, 'technicalAttributeSelect')}
					value={formik.values.technicalAttributeSelect?.label && formik.values.technicalAttributeSelect}
					options={technicalAttributeOptions}
					placeholder="Seleccione atributo técnico"
				/>
				<UfinetSelect
					requiredIcon
					className="col-4"
					labelTitle={intl.formatMessage({ id: 'COST.TRANSMISSION_MEDIUM' })}
					tooltipTitle={intl.formatMessage({ id: 'COST.TRANSMISSION_MEDIUM' })}
					error={formik.errors.transmissionMediumSelect?.label}
					isClearable
					isDisabled={!formik.values.serviceTypeSelect?.value}
					onChange={onChange(formik, 'transmissionMediumSelect')}
					value={formik.values.transmissionMediumSelect?.label && formik.values.transmissionMediumSelect}
					options={transmissionMediumOptions}
					placeholder="Seleccione medio de transmisión"
				/>
			</div>
			<UfinetActionButton ref={actionButtonRef} className="mt-10" content={intl.formatMessage({ id: 'SAVE' })} />
		</form>
	)
}
export { NewEquipmentModalBody }
